import React, { useEffect, useState } from 'react';
import { Card, Row } from 'antd';
import HubSpot from '../components/HubSpot'
import DealEconomicsSummaries from '../components/DealEconomicsSummaries';
import { getHubspotDealDetails, getLoggedInUserDetails, getPricingDealVersions, GetServiceLineList } from '../../services/Apis';
import { useLocation } from 'react-router-dom';
import { HubSpotData } from '../interfaces/interfaces';
import dayjs from 'dayjs';
import LogoutPopUp from '../../components/LogoutPopUp';
import { LoadingOutlined } from '@ant-design/icons';
import NoAccessPopup from '../../components/NoAccessPopup';
import { currencySymbolsMap, useCurrency } from '../../context/CurrencyContext';


const VersionManagement: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const dealId: any = queryParams.get('deal_id');
  const { updateCurrency } = useCurrency();
  
  const [response, setData] = useState<any>(null);
  const [versionDetails, setVersionDetails] = useState<any[]>([]);
  const [tokenExpired, setTokenExpired] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [enabledFeatureList, setEnabledFeatureList] = useState<string[]>([])
  const [distinctServiceLines, setDistinctServiceLines] = useState<string[]>([]);
  const [noAccess, setNoAccess] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      const featureList: any = await getLoggedInUserDetails(dealId);
      setEnabledFeatureList(featureList.data.features);

      const result = await getHubspotDealDetails(dealId);
      updateCurrency(result.currency);  
      const serviceLines = await GetServiceLineList(dealId);
      if (result.error_status === 401 || serviceLines.error_status === 401) {
        setTokenExpired(true);
      } else if (result.error_status === 403 || serviceLines.error_status === 403) {
        setNoAccess(true);
        return
      } else {
        if (result.error) {
          console.error('Error Response Data:', result.error);
          console.error('Error Response Status:', result.error_status);
          setError(result.error);
        }
        if (serviceLines.error) {
          console.error('Error Response Data:', serviceLines.error);
          console.error('Error Response Status:', serviceLines.error_status);
          setError(serviceLines.error);
        }
      }
      const distinctServiceLines: string[] = Array.from(new Set(serviceLines.map((obj: any) => obj.service_line_code)));
      setDistinctServiceLines([...distinctServiceLines, "Contingency"]);


      setData(result);
      const versionsApi = await getPricingDealVersions(Number(dealId));

      if (versionsApi.error_status === 401) {
        setTokenExpired(true);
      } else if (versionsApi.error_status === 403) {
        setNoAccess(true);
        return
      } else {
        if (versionsApi.error) {
          console.error('Error Response Data:', versionsApi.error);
          console.error('Error Response Status:', versionsApi.error_status);
          setError(versionsApi.error);
        }
      }
      setVersionDetails(versionsApi);
    };

    fetchData();
  }, []);

  if (noAccess || ((!response || !versionDetails) && !tokenExpired )) {

      return <>{noAccess ? <NoAccessPopup openPopup={true} popupMessage={'You do not have access to view pricing versions for this deal'}/>:
      <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.7)', // Semi-transparent white
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999, // Ensure the loader is on top
      }}
    >
      <LoadingOutlined style={{ fontSize: "40px", color: "cornflowerblue" }} />
    </div>}</>;
  }
  const dealDetails: HubSpotData = {
    'Deal Name': response.deal_name,
    'bpv': '',
    'Client Name': response.company_name,
    'HubSpot ID': response.deal_id,
    'Deal Owner': response.deal_owner_name,
    'Deal Stage': response.deal_stage_name,
    'Engagement Type': response.engagement_type,
    'Expected Start Date': new Date(response.project_start_date).toLocaleDateString(), // Format as needed
    'Close Date': response.project_close_date ? new Date(response.project_close_date).toLocaleDateString() : response.project_close_date, // Format as needed
    'Duration': `${response.duration_in_months} months`,
    'Deal Collaborators': response?.deal_collaborators.map((item: any) => item.email),
    'Work Ahead': response.work_ahead,
    'Currency': response.currency,
    '2026 Book': response.book_2026_name,
    'Offering': response.offering ?? '',
    'Deal Description': response.description ?? '',
    'Tech Involved': response.tech_involved ?? '',
    'pdc': response?.pricing_contributors.map((item: any) => item.email),
    'Deal Amount in HubSpot': response.deal_amount,
    'Delivery Lead Name': response.delivery_lead_name,
    'Solution Lead Name': response.solution_lead_name,
    'Deal Line Items': response.hubspot_line_items,
    'Hubspot Link': response.hubspot_link,
    'Resource Request Sent On': response.resource_request_sent_on,
    'Locked Version Name': response.locked_version_name,
    'HS Last Updated Version Name': response.hs_last_updated_version_name,
    'Deal Locked On': response.deal_locked_on,
    'Version Id': response.version_id,
    'Locked Version Id': response.locked_version_id,
    'Pricing Version Project Start Date': new Date(response.project_start_date).toLocaleDateString(),
    'Pricing Version Duration': response.duration_in_months,
    'Pricing Version Engagement Type': response.engagement_type,
    'Last Refreshed On': dayjs(response.last_refreshed_on).tz('America/New_York').format('ddd, MMM D, YYYY, h:mm:ss A z'),
  };
  // Helper function to format numbers as currency
  const formatCurrency = (amount: number, currency: string): string => {
    try {
      return `${currencySymbolsMap[currency] || '$'} ${amount.toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
    } catch (error) {
      console.error(error);
      return `${currencySymbolsMap[currency] || '$'} 0`;
    }
  };
  const dealEconomicsSummaries = versionDetails.map((version: any) => ({
    'Blended Hourly Price': formatCurrency(version.blend_hourly_price, version.currency),
    'Total Project Price': formatCurrency(version.total_project_price, version.currency),
    'Project Discount Amount': formatCurrency(version.project_discount_amount, version.currency),
    'Net Project Price': formatCurrency(version.net_project_price, version.currency),
    'Total Project Labor Cost': formatCurrency(version.total_project_labor_cost, version.currency),
    'Projected Client Margin %': `${version.project_client_margin ?? 0}%`,
    'Target Client Margin %': `${version.target_client_margin ?? 0}%`,
    'Variance to Target Margin': `${version.variance_to_target_margin ?? 0}%`,
    'Total Billable Hours': version.total_billable_hours,
    'Updated By': version.updated_by,
    'Updated On': dayjs(version.updated_on).tz('America/New_York').format('MMM D, YYYY, h:mm:ss A z'),
    'ODE Status': version.ode_status
  }));

  const headerList = versionDetails.map((version: any) => version.version_name);


  return (
    <>
      {tokenExpired ? <LogoutPopUp openPopup={tokenExpired} /> :

        <div>
          {error && <p className='text-danger'>Error: {error}</p>}

          <HubSpot disableForm={false} hubSpotData={dealDetails} pricingContributors={[]} onChange={console.log} versionName={""} pricingVersion={""} pricingCollaborators={[]} onUpdatedVersionName={console.log} readOnly={true} isDealLocked={false} setDealLock={() => { }} enabledFeatureList={enabledFeatureList} setSaveNewPricingVersion={() => { }} setlockOde={() => { }} setSendEmail={() => { }} lineItemData={[]} dealId={dealId} distinctServiceLines={distinctServiceLines} setReloadNavigation={() => { }} lastRefreshedOn={dealDetails['Last Refreshed On']}/>

          <Row gutter={16}>
            <DealEconomicsSummaries span={8} dealId={dealId} dealEconomicsSummaries={dealEconomicsSummaries} headerList={headerList} enabledFeatureList={enabledFeatureList}/>
          </Row>
        </div>
      }
    </>
  );
};

export default VersionManagement;